import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth";
import { Logo, LogoLight } from "../components/Logo";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refCode = params.get("ref") || "";
  const isSucc = (params.get("context") || "").toLowerCase() === "succession";
  const [form, setForm] = useState({
    prenom: "",
    nom: "",
    date_naissance: "",
    email: "",
    password: "",
  });
  const [accept, setAccept] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!accept) {
      setError("Veuillez accepter la politique de confidentialité.");
      return;
    }
    if (!form.prenom.trim() || !form.nom.trim()) {
      setError("Veuillez renseigner votre prénom et votre nom.");
      return;
    }
    if (!form.date_naissance) {
      setError("Veuillez renseigner votre date de naissance.");
      return;
    }
    if (form.password.length < 8) {
      setError("Mot de passe trop court (8 caractères minimum).");
      return;
    }
    setLoading(true);
    const full_name = `${form.prenom.trim()} ${form.nom.trim()}`.trim();
    const payload = {
      full_name,
      prenom: form.prenom.trim(),
      nom: form.nom.trim(),
      date_naissance: form.date_naissance,
      email: form.email,
      password: form.password,
      accept_privacy: accept,
      consent_marketing: consentMarketing,
      consent_date: consentMarketing ? new Date().toISOString() : null,
    };
    if (refCode) payload.referral_code = refCode;
    const res = await register(payload);
    setLoading(false);
    if (res.ok) {
      // Nouveau flux : choix d'espace juste après création de compte
      nav("/choix-espace");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{ backgroundColor: isSucc ? "#123B36" : "var(--auprea-cream)" }} data-testid={isSucc ? "register-succession-context" : "register-default"}>
      <div className="w-full max-w-md">
        <Link to="/" className="block mb-8 text-center">
          {isSucc ? <LogoLight size={36} /> : <Logo size={36} />}
        </Link>
        {isSucc && (
          <div style={{ background: "rgba(173,124,51,0.15)", border: "1px solid rgba(173,124,51,0.35)", color: "#F6F2E9", padding: "12px 16px", borderRadius: 6, marginBottom: 18, fontSize: 13.5, lineHeight: 1.55 }} data-testid="register-succession-banner">
            <strong style={{ color: "#E1BA6C" }}>Votre diagnostic est sauvegardé.</strong>{" "}
            Créez votre espace pour retrouver votre dossier à tout moment.
          </div>
        )}
        <div className="auprea-card" style={isSucc ? { background: "#FFFDF7", borderColor: "rgba(173,124,51,0.25)" } : undefined}>
          <h1 style={{ fontSize: 28, marginBottom: 6, color: isSucc ? "#123B36" : undefined }}>
            {isSucc ? "Créer mon espace succession" : "Créer mon carnet"}
          </h1>
          <p style={{ color: "rgba(35,38,46,0.7)", marginBottom: 28, fontSize: 15 }}>
            {isSucc
              ? "Quelques informations pour retrouver votre dossier et débloquer la feuille de route."
              : "Quelques informations pour démarrer. Forfait Découverte gratuit."}
          </p>
          {refCode && (
            <div
              style={{ marginBottom: 16, padding: "10px 14px", backgroundColor: "rgba(202,168,106,0.15)", border: "1px solid var(--auprea-gold)", fontSize: 14 }}
              data-testid="referral-applied"
            >
              🎁 Vous avez été parrainé(e) (code <strong>{refCode}</strong>) — votre parrain sera récompensé après votre premier paiement.
            </div>
          )}
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="auprea-label">Prénom *</label>
                <input
                  type="text"
                  className="auprea-input"
                  value={form.prenom}
                  onChange={(e) => setForm({ ...form, prenom: e.target.value })}
                  required
                  autoComplete="given-name"
                  data-testid="register-prenom"
                />
              </div>
              <div>
                <label className="auprea-label">Nom *</label>
                <input
                  type="text"
                  className="auprea-input"
                  value={form.nom}
                  onChange={(e) => setForm({ ...form, nom: e.target.value })}
                  required
                  autoComplete="family-name"
                  data-testid="register-nom"
                />
              </div>
            </div>
            <div>
              <label className="auprea-label">Date de naissance *</label>
              <input
                type="date"
                className="auprea-input"
                value={form.date_naissance}
                onChange={(e) => setForm({ ...form, date_naissance: e.target.value })}
                required
                max={new Date().toISOString().split("T")[0]}
                data-testid="register-dob"
              />
            </div>
            <div>
              <label className="auprea-label">Email *</label>
              <input
                type="email"
                className="auprea-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="register-email"
              />
            </div>
            <div>
              <label className="auprea-label">Mot de passe (8 caractères min.) *</label>
              <input
                type="password"
                className="auprea-input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                minLength={8}
                data-testid="register-password"
              />
            </div>
            <label className="flex items-start gap-3 text-sm" style={{ lineHeight: 1.5 }}>
              <input
                type="checkbox"
                checked={accept}
                onChange={(e) => setAccept(e.target.checked)}
                style={{ marginTop: 3 }}
                data-testid="register-privacy"
              />
              <span>
                J&apos;accepte la <a href="/confidentialite" style={{ color: "var(--auprea-blue)" }}>politique de confidentialité</a> et le traitement de mes données par AUPREA conformément au RGPD.
              </span>
            </label>
            <label className="flex items-start gap-3 text-sm" style={{ lineHeight: 1.5 }} data-testid="register-consent-marketing-wrap">
              <input
                type="checkbox"
                checked={consentMarketing}
                onChange={(e) => setConsentMarketing(e.target.checked)}
                style={{ marginTop: 3 }}
                data-testid="register-consent-marketing"
              />
              <span>
                J&apos;accepte de recevoir des conseils et des rappels d&apos;AUPRÉA par email pour avancer sur ma succession. Je peux me désinscrire à tout moment.
              </span>
            </label>
            {error && (
              <div
                style={{ color: "#b53a3a", fontSize: 14, padding: "10px 12px", backgroundColor: "#fcf3f3", border: "1px solid #f0c5c5" }}
                data-testid="register-error"
              >
                {error}
              </div>
            )}
            <button type="submit" className="auprea-btn w-full" disabled={loading} data-testid="register-submit">
              {loading ? "Création…" : "Créer mon compte"}
            </button>
          </form>
          <div className="mt-6 text-center text-sm" style={{ color: "rgba(35,38,46,0.7)" }}>
            Déjà inscrit ?{" "}
            <Link to="/connexion" style={{ color: "var(--auprea-blue)", fontWeight: 500 }} data-testid="link-login">
              Se connecter
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
